/**
 * Arbejdsdage og helligdage - naevneren i faktureringsgraden (F5 i
 * docs/dashboard/1-1-Storskaerm-Dashboard-Designnotat.md).
 *
 * **Regnet, ikke tastet ind.** Designnotatet foreslog de danske helligdage for 2026-2028 lagt ind i
 * haanden. De bevaegelige helligdage foelger paasken, og paasken kan regnes, saa kalenderen staar
 * her som en regel i stedet for en liste, der loeber ud i 2029.
 *
 * **Kun helligdagene i loven.** Store bededag er afskaffet fra 2024. Grundlovsdag, juleaftensdag og
 * nytaarsaftensdag er ikke helligdage, selvom mange holder fri - er de fridage hos OneCode, hoerer
 * de til i `danishHolidays`.
 *
 * **ISO-strenge, regnet i UTC.** `Date` i lokal tid ville flytte en dato, naar serverens tidszone
 * ikke er dansk - og en container koerer typisk i UTC.
 */

const DAY_MS = 86_400_000;

function utcMs(iso: string): number {
  const [year, month, day] = iso.slice(0, 10).split('-').map(Number);
  return Date.UTC(year ?? 0, (month ?? 1) - 1, day ?? 1);
}

function isoOf(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

function shift(iso: string, days: number): string {
  return isoOf(utcMs(iso) + days * DAY_MS);
}

/** Paaskedag. Den gregorianske computus (Meeus/Jones/Butcher). */
export function easterSunday(year: number): string {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return isoOf(Date.UTC(year, month - 1, day));
}

export interface Holiday {
  /** ISO-dato. */
  date: string;
  /** Dansk navn, som /admin/tv-dashboard viser det. */
  name: string;
}

const holidayCache = new Map<number, Holiday[]>();

/**
 * Aarets helligdage i datoorden. Paaskedag og pinsedag er altid en soendag og aendrer derfor
 * ikke antallet af arbejdsdage - de staar her, saa listen er hel.
 */
export function danishHolidays(year: number): Holiday[] {
  const cached = holidayCache.get(year);
  if (cached) return cached;

  const easter = easterSunday(year);
  const holidays: Holiday[] = [
    { date: `${year}-01-01`, name: 'Nytårsdag' },
    { date: shift(easter, -3), name: 'Skærtorsdag' },
    { date: shift(easter, -2), name: 'Langfredag' },
    { date: easter, name: 'Påskedag' },
    { date: shift(easter, 1), name: '2. påskedag' },
    { date: shift(easter, 39), name: 'Kristi himmelfartsdag' },
    { date: shift(easter, 49), name: 'Pinsedag' },
    { date: shift(easter, 50), name: '2. pinsedag' },
    { date: `${year}-12-25`, name: 'Juledag' },
    { date: `${year}-12-26`, name: '2. juledag' },
  ].sort((a, b) => a.date.localeCompare(b.date));

  holidayCache.set(year, holidays);
  return holidays;
}

function isHoliday(iso: string): boolean {
  const year = Number(iso.slice(0, 4));
  return danishHolidays(year).some((holiday) => holiday.date === iso);
}

/** Mandag til fredag og ikke en helligdag. */
export function isWorkingDay(iso: string): boolean {
  const weekday = new Date(utcMs(iso)).getUTCDay();
  if (weekday === 0 || weekday === 6) return false;
  return !isHoliday(iso.slice(0, 10));
}

/** Arbejdsdage fra og med `start` til og med `end`. Nul, naar `end` ligger foer `start`. */
export function workingDaysBetween(start: string, end: string): number {
  let count = 0;
  for (let ms = utcMs(start), last = utcMs(end); ms <= last; ms += DAY_MS) {
    if (isWorkingDay(isoOf(ms))) count += 1;
  }
  return count;
}

/** De helligdage, der falder paa en hverdag i perioden - dem, der koster arbejdsdage. */
export function weekdayHolidaysBetween(start: string, end: string): Holiday[] {
  const from = start.slice(0, 10);
  const to = end.slice(0, 10);
  const years: number[] = [];
  for (let year = Number(from.slice(0, 4)); year <= Number(to.slice(0, 4)); year += 1) {
    years.push(year);
  }

  return years
    .flatMap((year) => danishHolidays(year))
    .filter((holiday) => holiday.date >= from && holiday.date <= to)
    .filter((holiday) => {
      const weekday = new Date(utcMs(holiday.date)).getUTCDay();
      return weekday !== 0 && weekday !== 6;
    });
}
